import { useEffect, useRef, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { captureAdAttribution } from './lib/adAttribution';
import { trackMetaEvent } from './lib/meta';
import { getOnboardingStatus } from './lib/api';
import LandingPage from './LandingPage';
import OnboardingPage from './OnboardingPage';
import PostSignupPage from './PostSignupPage';
import MarketingOnboardingPage from './MarketingOnboardingPage';
import MarketingCharacterOnboardingPage from './MarketingCharacterOnboardingPage';
import ProjectsPage from './ProjectsPage';
import AccountPage from './AccountPage';
import InviteAcceptPage from './InviteAcceptPage';
import PricingPage from './PricingPage';
import BlogPage from './BlogPage';
import BlogPostPage from './BlogPostPage';
import DocsPage from './DocsPage';
import UnsubscribePage from './UnsubscribePage';
import ReferralLandingPage from './ReferralLandingPage';
import ReferralActivatePage from './ReferralActivatePage';
import DeepLinkHandler from './components/DeepLinkHandler';
import SEOHead from './components/SEOHead';
import AuthModal from './components/AuthModal';
import OnboardingSupportChat from './components/OnboardingSupportChat';
import ReferralCodePrompt from './components/ReferralCodePrompt';
import ReferralSharePrompt from './components/ReferralSharePrompt';
import SubscriptionModal from './components/SubscriptionModal';
import { useAuthContext } from './components/AuthProvider';
import AcademicPapersPage from './AcademicPapersPage';
import StudioPage from './StudioPage';
import UgcPage from './UgcPage';
import HookChangerPage from './HookChangerPage';
import VideoVariantTemplatePage from './VideoVariantTemplatePage';
import ScratchVideoTemplatePage from './ScratchVideoTemplatePage';
import AssetsPage from './AssetsPage';
import AssetDetailPage from './AssetDetailPage';
import VideosPage from './VideosPage';
import CreateVideoPage from './CreateVideoPage';
import ReelsSearchPage from './ReelsSearchPage';
import MetaAdsDashboardPage from './MetaAdsDashboardPage';
import CharactersPage from './CharactersPage';
import CharacterDetailPage from './CharacterDetailPage';
import VoicesPage from './VoicesPage';
import IntegrationsPage from './IntegrationsPage';
import SlackAgentLinkPage from './SlackAgentLinkPage';
import BrainPage from './BrainPage';
import ExamplesPage from './ExamplesPage';
import UgcMobilePage from './UgcMobilePage';
import { PrivacyPolicyPage, TermsOfServicePage } from './LegalPage';
import TemplatesPage from './TemplatesPage';
import { SpacesIndexPage, SpaceDetailPage } from './SpacesPage';
import { isFrontendDevToolsEnabled } from './devTools/devApi';
import './colors.css';
import './App.css';
import './SidebarPages.css';

// Lazy: pulls in the @hyperframes/studio bundle — keep it out of the main chunk.
const HfSandboxPage = lazy(() => import('./HfSandboxPage'));
const WorkflowDetailPage = lazy(() => import('./WorkflowDetailPage'));
const WorkflowsListPage = lazy(() => import('./WorkflowsPage').then((module) => ({ default: module.WorkflowsListPage })));
const LegacyWorkflowBuilderPage = lazy(() => import('./WorkflowsPage').then((module) => ({ default: module.WorkflowBuilderPage })));
const WorkflowRunPage = lazy(() => import('./WorkflowsPage').then((module) => ({ default: module.WorkflowRunPage })));
const WorkflowRunsRedirect = lazy(() => import('./WorkflowsPage').then((module) => ({ default: module.WorkflowRunsRedirect })));
const GeminiVideoChatDevPage = lazy(() => import('./devTools/GeminiVideoChatDevPage'));
const DecompositionLabDevPage = lazy(() => import('./devTools/DecompositionLabDevPage'));
const ComplexVideoEditLabDevPage = lazy(() => import('./devTools/ComplexVideoEditLabDevPage'));
const WORKFLOW_RUN_FIRST_ENABLED = process.env.REACT_APP_WORKFLOW_RUN_FIRST !== 'false';
const DEV_TEST_PAGES_ENABLED = isFrontendDevToolsEnabled();

function ReferralCapture() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    if (refCode) {
      localStorage.setItem('latted_referral_code', refCode.trim().toUpperCase());
      // Clean ref param from URL without reload
      const url = new URL(window.location);
      url.searchParams.delete('ref');
      window.history.replaceState({}, '', url.pathname + url.search);
    }
  }, []);
  return null;
}

function AdAttributionCapture() {
  const location = useLocation();

  // Capture fbclid + UTM params on initial load AND whenever search params change
  // (handles SPA navigation with new fbclid/utm params)
  useEffect(() => {
    captureAdAttribution();
  }, [location.search]);

  // Fire PageView on every route change (SPA-aware)
  useEffect(() => {
    trackMetaEvent('PageView');
  }, [location.pathname]);

  return null;
}

function getPendingOrganizationInviteToken() {
  try {
    return sessionStorage.getItem('latted_pending_org_invite') || '';
  } catch {
    return '';
  }
}

function ReferralRedirect() {
  const { user, loading } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectedRef = useRef(false);

  useEffect(() => {
    const hasPendingOrganizationInvite = getPendingOrganizationInviteToken();
    if (loading || !user || redirectedRef.current || location.pathname === '/invite/accept' || hasPendingOrganizationInvite) return;

    // Redirect to activate page if user just signed up via a referral link
    const hasReferralCode = localStorage.getItem('latted_referral_code');
    const hasReferralApplied = localStorage.getItem('latted_referral_applied');

    if (hasReferralCode || hasReferralApplied) {
      const createdAt = new Date(user.created_at);
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
      if (createdAt >= fiveMinutesAgo) {
        redirectedRef.current = true;
        localStorage.removeItem('latted_referral_applied');
        navigate('/referral/activate', { replace: true });
      }
    }
  }, [user, loading, navigate, location.pathname]);

  return null;
}

function PendingOrganizationInviteRedirect() {
  const { user, loading } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (loading || !user || location.pathname === '/invite/accept') return;
    const inviteToken = getPendingOrganizationInviteToken();
    if (inviteToken) {
      navigate('/invite/accept', { replace: true });
    }
  }, [loading, location.pathname, navigate, user]);

  return null;
}

const POST_SIGNUP_SKIP_PATHS = [
  '/post-signup',
  '/onboarding/marketing',
  '/referral/activate',
  '/invite/accept',
  '/settings/integrations/slack-agent/link',
  // Pages that handle post-auth flows themselves; don't interrupt them.
  '/ugc/create',
];

function shouldSkipPostSignup(pathname) {
  return POST_SIGNUP_SKIP_PATHS.includes(pathname) ||
    pathname.startsWith('/templates/') ||
    (DEV_TEST_PAGES_ENABLED && pathname.startsWith('/dev/'));
}

function getPostSignupAnsweredKey(userId) {
  return userId ? `latted_post_signup_answered:${userId}` : null;
}

function getPostSignupNeededKey(userId) {
  return userId ? `latted_needs_post_signup:${userId}` : null;
}

function PostSignupGate({ children }) {
  const { user, loading } = useAuthContext();
  const location = useLocation();
  const [redirected, setRedirected] = useState(false);
  const [isCheckingOnboarding, setIsCheckingOnboarding] = useState(false);
  const [hasAnsweredPostSignup, setHasAnsweredPostSignup] = useState(null);

  // Brand-new signups only: created_at and last_sign_in_at within 5s of each other.
  // We still verify whether the post-signup use-type question was already answered
  // before redirecting, since the auth timestamps stay "new" across refreshes.
  const isNewUser = !!(user?.created_at && user?.last_sign_in_at &&
    Math.abs(new Date(user.created_at) - new Date(user.last_sign_in_at)) < 5000);

  useEffect(() => {
    if (loading || !user || !isNewUser) {
      setIsCheckingOnboarding(false);
      setHasAnsweredPostSignup(true);
      return;
    }

    let cancelled = false;
    setIsCheckingOnboarding(true);
    setHasAnsweredPostSignup(null);

    const localAnsweredKey = getPostSignupAnsweredKey(user.id);
    const localNeededKey = getPostSignupNeededKey(user.id);
    const locallyAnswered = localAnsweredKey
      ? localStorage.getItem(localAnsweredKey) === 'true'
      : false;
    if (locallyAnswered) {
      setHasAnsweredPostSignup(true);
      setIsCheckingOnboarding(false);
      return () => {
        cancelled = true;
      };
    }

    const locallyNeedsPostSignup = localNeededKey
      ? localStorage.getItem(localNeededKey) === 'true'
      : false;
    if (locallyNeedsPostSignup) {
      setHasAnsweredPostSignup(false);
      setIsCheckingOnboarding(false);
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      try {
        const status = await getOnboardingStatus();
        if (cancelled) return;
        const answered = Boolean(status?.useType ?? status?.use_type);
        setHasAnsweredPostSignup(answered);
        if (answered && localAnsweredKey) {
          localStorage.setItem(localAnsweredKey, 'true');
        }
        if (localNeededKey) {
          if (answered) {
            localStorage.removeItem(localNeededKey);
          } else {
            localStorage.setItem(localNeededKey, 'true');
          }
        }
      } catch {
        if (cancelled) return;
        // Fail open. Post-signup routing is helpful, but should never trap the
        // user in a redirect loop because auth/session bootstrap was delayed.
        setHasAnsweredPostSignup(true);
      } finally {
        if (!cancelled) {
          setIsCheckingOnboarding(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loading, user, isNewUser]);

  const hasPendingOrganizationInvite = getPendingOrganizationInviteToken();
  const needsRedirect = !loading && !isCheckingOnboarding && isNewUser && hasAnsweredPostSignup === false && !redirected && !hasPendingOrganizationInvite &&
    !shouldSkipPostSignup(location.pathname);

  useEffect(() => {
    if (needsRedirect) setRedirected(true);
  }, [needsRedirect]);

  if (needsRedirect) return <Navigate to="/post-signup" replace />;
  return children;
}

function OnboardingSupportChatMount() {
  const { user, loading } = useAuthContext();
  const location = useLocation();
  const isOnboardingRoute = (
    location.pathname === '/post-signup' ||
    location.pathname.startsWith('/onboarding')
  );

  if (loading || !user || !isOnboardingRoute) return null;
  return <OnboardingSupportChat />;
}

const SUB_MODAL_SHOWN_KEY = 'latted_sub_modal_shown';
const PENDING_TIER_KEY = 'latted_pending_subscribe_tier';

function GlobalAuthModal() {
  const { user, loading, authModalOpen, closeAuthModal, signInWithGoogle, signInWithEmail } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const wasAuthModalOpen = useRef(false);
  const [showSubModal, setShowSubModal] = useState(false);

  // After OAuth redirect: if there's a pending tier, show subscription modal immediately
  useEffect(() => {
    if (!user || loading) return;
    const pendingTier = localStorage.getItem(PENDING_TIER_KEY);
    if (pendingTier) {
      localStorage.removeItem(PENDING_TIER_KEY);
      setShowSubModal(true);
    }
  }, [user, loading]);

  // After OAuth redirect: if there's a pending UGC wizard, return to /ugc/create.
  // The wizard state (including File objects) is persisted in IndexedDB by
  // UgcMobilePage and restored there.
  useEffect(() => {
    if (!user || loading) return;
    if (location.pathname === '/ugc/create') return;
    try {
      const pendingUgc = sessionStorage.getItem('latted_pending_ugc');
      if (pendingUgc) {
        navigate('/ugc/create', { replace: true });
      }
    } catch { /* ignore */ }
  }, [user, loading, location.pathname, navigate]);

  useEffect(() => {
    // User just signed in via auth modal
    if (wasAuthModalOpen.current && user) {
      // Don't redirect away from pages that handle post-auth themselves
      const skipRedirectPaths = ['/ugc/create', '/settings/integrations/slack-agent/link'];
      const isTemplatePath = location.pathname.startsWith('/templates/');
      const isDevelopmentToolPath = DEV_TEST_PAGES_ENABLED && location.pathname.startsWith('/dev/');
      if (!skipRedirectPaths.includes(location.pathname) && !isTemplatePath && !isDevelopmentToolPath && location.pathname !== '/') {
        navigate('/', { replace: true });
      }
      // Show subscription modal once per user
      const shownFor = localStorage.getItem(SUB_MODAL_SHOWN_KEY);
      if (shownFor !== user.id) {
        setShowSubModal(true);
        localStorage.setItem(SUB_MODAL_SHOWN_KEY, user.id);
      }
    }
    wasAuthModalOpen.current = authModalOpen;
  }, [user, authModalOpen, navigate, location.pathname]);

  return (
    <>
      <AuthModal
        isOpen={!loading && !user && authModalOpen}
        onClose={closeAuthModal}
        onSignIn={() => signInWithGoogle()}
        onEmailSignIn={signInWithEmail}
      />
      <SubscriptionModal
        isOpen={showSubModal}
        onClose={() => setShowSubModal(false)}
      />
    </>
  );
}

function App() {
  return (
    <Router>
      <SEOHead />
      <ReferralCapture />
      <AdAttributionCapture />
      <PendingOrganizationInviteRedirect />
      <ReferralRedirect />
      <GlobalAuthModal />
      <OnboardingSupportChatMount />
      <ReferralCodePrompt />
      <ReferralSharePrompt />
      <PostSignupGate>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/post-signup" element={<PostSignupPage />} />
          <Route path="/onboarding/marketing" element={<MarketingOnboardingPage />} />
          <Route path="/onboarding/marketing/character" element={<MarketingCharacterOnboardingPage />} />
          <Route path="/onboarding/:projectId" element={<OnboardingPage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/templates" element={<TemplatesPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/academic-papers" element={<AcademicPapersPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/studio" element={<StudioPage />} />
          <Route path="/ugc" element={<UgcPage />} />
          <Route path="/templates/ugc-ads" element={<UgcPage />} />
          <Route path="/templates/hook-changer" element={<HookChangerPage />} />
          <Route path="/templates/change-avatar" element={<VideoVariantTemplatePage templateId="change-avatar" />} />
          <Route path="/templates/change-style" element={<VideoVariantTemplatePage templateId="change-style" />} />
          <Route path="/templates/change-cta" element={<VideoVariantTemplatePage templateId="change-cta" />} />
          <Route path="/templates/localize-ad" element={<VideoVariantTemplatePage templateId="localize-ad" />} />
          <Route path="/templates/zack-d-style-shorts" element={<ScratchVideoTemplatePage templateId="zack-d-style-shorts" />} />
          <Route path="/templates/fern-style-youtube-video" element={<ScratchVideoTemplatePage templateId="fern-style-youtube-video" />} />
          <Route path="/templates/saas-product-demo" element={<ScratchVideoTemplatePage templateId="saas-product-demo" />} />
          <Route path="/templates/studio-talking-head" element={<StudioPage />} />
          <Route path="/ugc/create" element={<UgcMobilePage />} />
          <Route path="/assets" element={<AssetsPage />} />
          <Route path="/assets/:assetId" element={<AssetDetailPage />} />
          <Route path="/spaces" element={<SpacesIndexPage />} />
          <Route path="/spaces/:spaceId" element={<SpaceDetailPage />} />
          <Route path="/spaces/:spaceId/folders/:folderId" element={<SpaceDetailPage />} />
          <Route path="/videos" element={<VideosPage />} />
          <Route path="/videos/create" element={<CreateVideoPage />} />
          <Route path="/reels" element={<ReelsSearchPage />} />
          <Route path="/meta-ads" element={<MetaAdsDashboardPage />} />
          <Route path="/characters" element={<CharactersPage />} />
          <Route path="/characters/:characterId" element={<CharacterDetailPage />} />
          <Route path="/voices" element={<VoicesPage />} />
          <Route path="/integrations" element={<IntegrationsPage />} />
          <Route path="/settings/integrations" element={<IntegrationsPage />} />
          <Route path="/settings/integrations/slack-agent" element={<IntegrationsPage />} />
          <Route path="/settings/integrations/slack-agent/link" element={<SlackAgentLinkPage />} />
          <Route path="/workflows" element={<Suspense fallback={null}><WorkflowsListPage /></Suspense>} />
          <Route path="/workflows/:workflowId" element={<Suspense fallback={null}>{WORKFLOW_RUN_FIRST_ENABLED ? <WorkflowDetailPage /> : <LegacyWorkflowBuilderPage />}</Suspense>} />
          <Route path="/workflows/:workflowId/runs" element={<Suspense fallback={null}><WorkflowRunsRedirect /></Suspense>} />
          <Route path="/workflows/runs/:runId" element={<Suspense fallback={null}><WorkflowRunPage /></Suspense>} />
          <Route
            path="/project/:projectId"
            element={(
              <Suspense fallback={null}>
                <HfSandboxPage />
              </Suspense>
            )}
          />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/invite/accept" element={<InviteAcceptPage />} />
          <Route path="/invite/:code" element={<ReferralLandingPage />} />
          <Route path="/referral/activate" element={<ReferralActivatePage />} />
          <Route path="/brain" element={<BrainPage />} />
          <Route path="/examples" element={<ExamplesPage />} />
          {DEV_TEST_PAGES_ENABLED ? (
            <>
              <Route path="/dev/gemini-video-chat" element={<Suspense fallback={null}><GeminiVideoChatDevPage /></Suspense>} />
              <Route path="/dev/decomposition-lab" element={<Suspense fallback={null}><DecompositionLabDevPage /></Suspense>} />
              <Route path="/dev/complex-video-edit-lab" element={<Suspense fallback={null}><ComplexVideoEditLabDevPage /></Suspense>} />
            </>
          ) : null}
          <Route path="/unsubscribe" element={<UnsubscribePage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="/collection/:collectionId" element={<DeepLinkHandler />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </PostSignupGate>
    </Router>
  );
}

export default App;
